import { supabase } from '../lib/supabase';

/**
 * Check if Supabase is properly configured
 */
export const isSupabaseConfigured = () => {
  return supabase !== null;
};

/**
 * Check Supabase connection health
 * @returns {Promise<{isHealthy: boolean, error: string|null}>}
 */
export const checkSupabaseHealth = async () => {
  if (!supabase) {
    return {
      isHealthy: false,
      error: 'Supabase is not configured. Please check your environment variables.'
    };
  }

  try {
    // Simple health check - try to query a system table
    const { error } = await supabase?.from('users')?.select('count', { count: 'exact', head: true });
    
    if (error) {
      // Check for common connectivity errors
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        return {
          isHealthy: false,
          error: 'Unable to connect to Supabase. Please check your internet connection.'
        };
      }
      
      return {
        isHealthy: false,
        error: error?.message || 'Database connection error'
      };
    }
    
    return { isHealthy: true, error: null };
  } catch (err) {
    return {
      isHealthy: false,
      error: 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Detect if an error is Supabase-related
 */
export const isSupabaseError = (error) => {
  if (!error) return false;
  
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorString = error?.toString()?.toLowerCase() || '';
  
  const supabaseKeywords = [
    'supabase',
    'postgrest',
    'database',
    'failed to fetch',
    'network error',
    'connection refused',
    'timeout',
    'cors',
    'unauthorized'
  ];
  
  return supabaseKeywords?.some(keyword => 
    errorMessage?.includes(keyword) || errorString?.includes(keyword)
  );
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred';
  
  const errorMessage = error?.message || error?.toString() || '';
  
  // Network errors
  if (errorMessage?.includes('Failed to fetch') || errorMessage?.includes('NetworkError')) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  
  // Authentication errors
  if (errorMessage?.includes('JWT') || errorMessage?.includes('token') || errorMessage?.includes('unauthorized')) {
    return 'Your session has expired. Please log in again.';
  }
  
  // Database errors
  if (errorMessage?.includes('violates') || errorMessage?.includes('constraint')) {
    return 'Unable to save data. Please check your input and try again.';
  }
  
  // Timeout errors
  if (errorMessage?.includes('timeout')) {
    return 'The request took too long. Please try again.';
  }
  
  // Generic Supabase errors
  if (isSupabaseError(error)) {
    return 'Database connection error. Please try again later.';
  }
  
  // Default
  return 'An unexpected error occurred. Please try again.';
};